import axios from 'axios';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Normalise the active i18n language to a backend-accepted lang code.
// Only 'sv' and 'en' are supported; anything else falls back to 'en'.
function _activeLang() {
    return i18n.language?.startsWith('sv') ? 'sv' : 'en';
}

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
// Email-first login gating — resolves the entering user's org auth methods.
// Returns { email_login_enabled, ms365_login_enabled }. Unknown emails get the
// permissive default (both true) — the backend never signals user existence.
export const getLoginOptions = (email) => api.post('/auth/login-options', { email });
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const changePassword = (data) => api.put('/auth/password', data);

// Projects
export const getProjects = (opts = {}) => api.get('/projects' + (opts.all ? '?all=true' : ''));
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const createActivity = (projectId, data) => api.post(`/projects/${projectId}/activities`, data);
export const updateActivity = (projectId, activityId, data) => api.put(`/projects/${projectId}/activities/${activityId}`, data);
export const deleteActivity = (projectId, activityId) => api.delete(`/projects/${projectId}/activities/${activityId}`);
export const getProjectCostSummary = () => api.get('/admin/projects/cost-summary');

// Deviation Types
export const getDeviationTypes = () => api.get('/deviation-types');
export const createDeviationType = (data) => api.post('/deviation-types', data);
export const updateDeviationType = (id, data) => api.put(`/deviation-types/${id}`, data);
export const deleteDeviationType = (id) => api.delete(`/deviation-types/${id}`);

// Cost Codes (Kostnadsställen)
export const getCostCodes = ({ type } = {}) =>
    api.get('/cost-codes', { params: type ? { type } : {} });
export const getMainCostCodes = () => getCostCodes({ type: 'main' });
export const createCostCode = (data) => api.post('/cost-codes', data);
export const updateCostCode = (id, data) => api.put(`/cost-codes/${id}`, data);
export const deleteCostCode = (id) => api.delete(`/cost-codes/${id}`);

// Time Entries
export const getTimeEntries = (params) => api.get('/time-entries', { params });
export const createTimeEntry = (data) => api.post('/time-entries', data);
export const updateTimeEntry = (id, data) => api.put(`/time-entries/${id}`, data);
export const deleteTimeEntry = (id) => api.delete(`/time-entries/${id}`);
export const bulkDeleteTimeEntries = (dates) =>
    api.delete('/time-entries/bulk', { data: { dates } });

// Flex Bank
export const getFlexBalance = () => api.get('/flex-balance');
export const getFlexTransactions = () => api.get('/flex-transactions');
export const adminFlexAdjustment = (data) => api.post('/admin/flex-adjustment', data);
export const getUserFlexTransactions = (userId) => api.get(`/admin/users/${userId}/flex-transactions`);

// Timesheet Status & Submission
export const getTimesheetStatus = (month, year) => api.get('/timesheets/status', { params: { month, year } });
export const getTimesheetNotes = (month, year) => api.get('/timesheets/notes', { params: { month, year } });
export const saveTimesheetNotes = (month, year, notes) => api.put('/timesheets/notes', { month, year, notes });
export const getOverdueStatus = () => api.get('/timesheets/overdue');
export const submitTimesheet = (month, year) => api.post('/timesheets/submit', { month, year });
export const getPendingTimesheets = () => api.get('/timesheets/pending');
export const getTimesheetHistory = () => api.get('/timesheets/history');
export const deleteTimesheet = (timesheetId) => api.delete(`/admin/timesheets/${timesheetId}`);
export const getMyTimesheetSubmissions = () => api.get('/timesheets/my-submissions');
export const reviewTimesheet = (data) => api.post('/timesheets/review', data);

// Timesheet Reopen Requests
export const requestTimesheetReopen = (data) => api.post('/timesheets/reopen-request', data);
export const getReopenRequests = () => api.get('/timesheets/reopen-requests');
export const reviewReopenRequest = (requestId, action) =>
    api.post(`/timesheets/reopen-requests/${requestId}/review`, null, { params: { action } });

// Admin-initiated send-back for an already-reviewed timesheet (Phase 14C / KD-076).
// Distinct from the employee-initiated reopen-request flow above — this sets the
// timesheet straight to "rejected" with the admin's comment, no employee approval step.
export const reopenTimesheet = (timesheetId, comment) =>
    api.post(`/timesheets/${timesheetId}/reopen`, { comment });

// Expenses (V2)
export const getExpenseTypes = () => api.get('/expense-types');
export const getExpenseHistory = () => api.get('/expenses/history');
export const getExpenseSettings = () => api.get('/expense-settings');
export const updateExpenseSettings = (data) => api.put('/admin/expense-settings', data);
export const getExpensesV2 = (params) => api.get('/expenses/v2', { params });
export const getExpenseReport = (month, year) => api.get('/expenses/report', { params: { month, year } });
export const createMileageExpense = (data) => api.post('/expenses/mileage', data);
export const createReceiptExpense = (data) => api.post('/expenses/receipt', data);
export const createAllowanceExpense = (data) => api.post('/expenses/allowance', data);
export const createBulkExpense = (dates, expenseType, expenseData) => api.post('/expenses/bulk', { dates, expense_type: expenseType, expense_data: expenseData });
export const getRecentMileage = (limit) =>
    api.get('/expenses/mileage/recent', { params: limit ? { limit } : undefined });
export const getAllowanceRecent = (limit) =>
    api.get('/expenses/allowance/recent', { params: limit ? { limit } : undefined });
export const updateMileageExpense = (id, data) => api.put(`/expenses/mileage/${id}`, data);
export const updateReceiptExpense = (id, data) => api.put(`/expenses/receipt/${id}`, data);
export const updateAllowanceExpense = (id, data) => api.put(`/expenses/allowance/${id}`, data);
export const removeReceiptAttachment = (id) => api.delete(`/expenses/receipt/${id}/attachment`);
export const uploadReceiptFile = (file, date) => {
    const form = new FormData();
    form.append('file', file);
    form.append('date', date);
    return api.post('/expenses/upload-receipt', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
export const deleteExpenseV2 = (id) => api.delete(`/expenses/v2/${id}`);
export const bulkDeleteExpenses = (dates, expenseTypes) =>
    api.delete('/expenses/bulk', { data: { dates, expense_types: expenseTypes } });
export const submitExpenseReport = (month, year) => api.post('/expenses/submit-report', null, { params: { month, year } });
export const requestExpenseReopen = (data) => api.post('/expenses/reopen-request', data);
export const getExpenseReopenRequests = () => api.get('/expenses/reopen-requests');
export const reviewExpenseReopenRequest = (requestId, action) =>
    api.post(`/expenses/reopen-requests/${requestId}/review?action=${action}`);
export const getAdminExpenseReports = (status) => api.get(`/admin/expense-reports${status ? `?status=${status}` : ''}`);
export const getAdminExpenseReportDetails = (userId, year, month) => api.get(`/admin/expense-reports/${userId}/${year}/${month}/details`);
export const reviewExpenseReport = (userId, year, month, action, comment) =>
    api.put(`/admin/expense-reports/${userId}/${year}/${month}/review`, null, { params: { action, comment } });
export const deleteExpenseReport = (userId, year, month) =>
    api.delete(`/admin/expense-reports/${userId}/${year}/${month}`);
// Admin-initiated send-back for an already-reviewed expense report (Phase 14C / KD-076).
// Distinct from the employee-initiated reopen-request flow above — this sets the
// report straight to "rejected" with the admin's comment, no employee approval step.
export const reopenExpenseReport = (userId, year, month, comment) =>
    api.post(`/admin/expense-reports/${userId}/${year}/${month}/reopen`, { comment });
export const getExpenseMonthlyStatus = (year, month) =>
    api.get('/admin/expense-reports/monthly-status', { params: { year, month } });

// Users
export const getUsers = (includeInactive = false) => api.get('/users', { params: { include_inactive: includeInactive } });
export const updateUser = (userId, data) => api.put(`/users/${userId}`, data);
export const updateMyLanguage = (language) => api.put('/auth/me/language', { language });
export const changeUserRole = (userId, role) => api.put(`/users/${userId}/role`, null, { params: { role } });
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// Vacation
export const getVacationBalance = () => api.get('/vacation/balance');
export const getUserVacationBalance = (userId) => api.get(`/vacation/balance/${userId}`);
export const depositVacationDays = (data) => api.post('/vacation/deposit', data);
export const useVacationDays = (data) => api.post('/vacation/use', data);
export const getVacationTransactions = () => api.get('/vacation/transactions');

// Vacation Planner
export const createVacationPlan = (data) => api.post('/vacation-plans', data);
export const getMyVacationPlans = () => api.get('/vacation-plans');
export const cancelVacationPlan = (planId) => api.delete(`/vacation-plans/${planId}`);
export const getAllVacationPlans = (status) => api.get(`/admin/vacation-plans${status ? `?status=${status}` : ''}`);
export const getVacationCalendar = (startDate, endDate) => api.get(`/admin/vacation-calendar?start_date=${startDate}&end_date=${endDate}`);
export const reviewVacationPlan = (planId, data) => api.put(`/admin/vacation-plans/${planId}/review`, data);

// Azure AD Auth
export const getAzureConfig = () => api.get('/auth/azure/config');
export const azureAuthCallback = (data) => api.post('/auth/azure/callback', data);

// Calendar
export const getRedDays = (year) => api.get('/calendar/red-days', { params: { year } });
export const getMonthInfo = (month, year) => api.get('/calendar/month-info', { params: { month, year } });
export const getMonthSummary = (year, month, userId) =>
    api.get('/calendar/month-summary', {
        params: userId ? { year, month, user_id: userId } : { year, month },
    });
export const lockCalendarWeek = (year, month, weekNumber) =>
    api.post('/calendar/lock', { lock_type: 'week', year, month, week_number: weekNumber });
export const unlockCalendarWeek = (year, month, weekNumber) =>
    api.delete('/calendar/lock', { data: { lock_type: 'week', year, month, week_number: weekNumber } });
export const lockCalendarMonth = (year, month) =>
    api.post('/calendar/lock', { lock_type: 'month', year, month });
export const unlockCalendarMonth = (year, month) =>
    api.delete('/calendar/lock', { data: { lock_type: 'month', year, month } });

// Reports
export const getSalaryReport = (month, year, userId) => 
    api.get('/reports/salary', { params: { month, year, user_id: userId } });
export const exportSalaryReport = (month, year) =>
    api.get('/reports/salary/export', { params: { month, year, lang: _activeLang() }, responseType: 'blob' });

// Billing Reports
export const getBillingReport = (month, year, projectId) =>
    api.get('/reports/billing', { params: { month, year, project_id: projectId } });
export const exportBillingReport = (month, year) =>
    api.get('/reports/billing/export', { params: { month, year, lang: _activeLang() }, responseType: 'blob' });

// Clients (Kunder) — admin/accountant/owner only (Phase 21, Customer Invoicing)
export const getClients = (params = {}) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deactivateClient = (id) => api.post(`/clients/${id}/deactivate`);
export const reactivateClient = (id) => api.post(`/clients/${id}/reactivate`);

// Invoices (Fakturor) — Phase 21, Customer Invoicing.
// `status`, `client_id`, `doc_type`, `year`, `month`, `limit` and `skip` are
// the accepted list filters. NOTE: `month` without `year` is a 400
// (error.invoices.invalid_period) — the caller must never send one alone.
export const getInvoices = (params = {}) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const generateInvoice = (data) => api.post('/invoices/generate', data);
// Draft only — the backend refuses an issued document with 409
// error.invoices.immutable (PRD 10.8). Corrections are credit notes.
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
// Allocates the invoice number and freezes the document — irreversible.
export const issueInvoice = (id) => api.post(`/invoices/${id}/issue`);
export const markInvoiceSent = (id) => api.post(`/invoices/${id}/mark-sent`);
// Prices an UNSAVED set of lines through the real engine (Task 23.9, PRD
// 12.8) — nothing here is persisted. Refused with 409 error.invoices.immutable
// once the invoice has left draft; the same per-line 422s a save would give.
export const pricePreviewInvoice = (id, data) => api.post(`/invoices/${id}/price-preview`, data);
// A draft renders on demand; an issued invoice is archived on its FIRST
// download and byte-identical on every later one (PRD 10.8), so `lang` only
// has an effect until that archive copy exists.
export const downloadInvoicePdf = (id, lang) =>
    api.get(`/invoices/${id}/pdf`, {
        params: { lang: lang || _activeLang() },
        responseType: 'blob',
    });
// Email an ISSUED invoice (PRD 10.11). `recipients` is 1–5 addresses the
// backend re-validates; a draft is refused with 409 error.invoices.not_issued.
//
// FAIL-CLOSED: a failure is ALWAYS an HTTP error, never a 200 carrying a
// "failed" status — so a resolved promise means the provider confirmed the
// send. The 200 body's `invoice` is the already re-read updated document
// (sent stamp included), so a caller must NOT re-fetch after a success.
//
// `lang` picks the covering email's language, and the PDF's only when this
// send is what freezes it — same rule as downloadInvoicePdf above.
export const sendInvoice = (id, recipients, optionsOrLang) => {
    let language = _activeLang();
    let attachment_ids = undefined;
    if (typeof optionsOrLang === 'string') {
        language = optionsOrLang || _activeLang();
    } else if (optionsOrLang && typeof optionsOrLang === 'object') {
        language = optionsOrLang.language || _activeLang();
        attachment_ids = optionsOrLang.attachment_ids;
    }
    const payload = {
        recipients,
        language,
    };
    if (attachment_ids !== undefined) {
        payload.attachment_ids = attachment_ids;
    }
    return api.post(`/invoices/${id}/send`, payload);
};

export const uploadInvoiceAttachment = (id, file, name) => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
        formData.append('name', name);
    }
    return api.post(`/invoices/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const deleteInvoiceAttachment = (id, attachmentId) =>
    api.delete(`/invoices/${id}/attachments/${attachmentId}`);

export const downloadInvoiceAttachment = (id, attachmentId) =>
    api.get(`/invoices/${id}/attachments/${attachmentId}`, {
        responseType: 'blob',
    });

// Payments (PRD 10.12). `data` is { amount, paid_date?, method?, reference?,
// note? } and the 200 body is the UPDATED invoice — including the
// server-computed `amount_due`, which is the only balance this product has.
// Never derive one in the browser.
//
// `amount` must always be a number. An empty numeric field is a 422 the user
// cannot act on, so the caller validates before sending.
//
// OVERPAYMENT IS ACCEPTED by design: an amount larger than the balance is
// registered in full and `amount_due` goes negative. Do not add a client-side
// guard against it — the money really did arrive, and blocking it would push
// the admin to type a figure the bank statement does not show.
export const registerInvoicePayment = (id, data) =>
    api.post(`/invoices/${id}/payments`, data);

// Reverse a payment. A POST to /void, deliberately NOT a DELETE: the row stays
// in `payments[]` forever with `voided_at` / `voided_by` / `void_reason` and
// simply stops counting towards settlement. There is no edit and no delete —
// a wrong payment is voided and a correct one registered alongside it.
export const voidInvoicePayment = (invoiceId, paymentId, reason) =>
    api.post(`/invoices/${invoiceId}/payments/${paymentId}/void`, {
        reason: reason || '',
    });

// Issue a credit note against an issued invoice (PRD 10.8) — the ONLY way to
// correct one, and irreversible. `options` accepts { invoice_date?, notes? }
// ONLY: the request model is `extra="forbid"`, so any other key is a 422.
//
// The 200 body is { credit_note, original, sources_reversed,
// sources_reconciled, source_conflicts, source_missing, source_failed }.
// `sources_reconciled: false` is NOT an error — the credit note is legally
// valid either way — but it means billed rows could not be returned to the
// billable pool and will never be re-billed, so the caller must surface it.
export const creditInvoice = (id, options = {}) =>
    api.post(`/invoices/${id}/credit`, options);

// Suppliers (Leverantörer) — admin/accountant/owner only
export const getSuppliers = () => api.get('/suppliers');
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);

// Supplier Invoices (Leverantörsfakturor) — admin/accountant/owner only
export const getSupplierInvoices = (year, month) =>
    api.get('/supplier-invoices', { params: { year, month } });
export const createSupplierInvoice = (data) => api.post('/supplier-invoices', data);
export const updateSupplierInvoice = (id, data) => api.put(`/supplier-invoices/${id}`, data);
export const deleteSupplierInvoice = (id) => api.delete(`/supplier-invoices/${id}`);
// Cross-month unpaid list (payment tracking) — every unpaid invoice in the
// tenant regardless of which month it belongs to, sorted by due_date asc.
export const getOutstandingSupplierInvoices = () => api.get('/supplier-invoices/outstanding');
// Cheap counts used for the sidebar badge — {unpaid, overdue}.
export const getUnpaidSupplierInvoiceCount = () => api.get('/supplier-invoices/unpaid-count');
export const uploadSupplierInvoiceFile = (file, year, month) => {
    const form = new FormData();
    form.append('file', file);
    form.append('year', year);
    form.append('month', month);
    return api.post('/supplier-invoices/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
// Phase 25.2 — reads a picked document's fields WITHOUT storing it, so the
// dialog can prefill its blank fields the moment the file is chosen. The file
// itself is still uploaded (above) at save time; nothing here leaves a file
// behind if the user cancels the dialog.
export const extractSupplierInvoiceFields = (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/supplier-invoices/extract', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
// Authenticated blob fetch for viewing an uploaded supplier invoice file
export const getSupplierInvoiceFileBlob = (filePath) =>
    api.get(`/supplier-invoices/file/${filePath}`, { responseType: 'blob' });
// Inbound documents (Phase 25.4) — mail that arrived at the intake mailbox and
// is HELD until a human turns it into a supplier invoice. Same roles as the
// supplier-invoice routes (require_accountant_or_admin).
// `status` reaches the other queues: 'error' (arrived but could not be read,
// still sitting in the mailbox), 'ignored', 'dismissed', 'confirmed', 'all'.
export const getInboundDocuments = (status = 'pending') =>
    api.get('/inbound-documents', { params: { status } });
// Cheap count used for the sidebar badge — {needs_review}.
export const getInboundDocumentCount = () => api.get('/inbound-documents/count');
// Records the supplier invoice a held document BECAME. The invoice is created
// first, through the ordinary POST /supplier-invoices with all its validation;
// this is the second of those two calls (see routes/inbound_documents.py).
export const confirmInboundDocument = (id, supplierInvoiceId) =>
    api.post(`/inbound-documents/${id}/confirm`, { supplier_invoice_id: supplierInvoiceId });
// A human deciding a held document is not wanted. Nothing is deleted — the row
// and its archived files stay, findable under status='dismissed'.
export const dismissInboundDocument = (id) => api.post(`/inbound-documents/${id}/dismiss`);
// A held document's file has NO supplier_invoices row yet, so
// getSupplierInvoiceFileBlob 404s on it — this is the endpoint that proves
// tenant ownership against inbound_documents instead. Serves the .eml too.
export const getInboundDocumentFileBlob = (filePath) =>
    api.get(`/inbound-documents/file/${filePath}`, { responseType: 'blob' });

export const downloadSupplierInvoicesReport = (year, month) =>
    api.get('/reports/supplier-invoices', { params: { year, month, lang: _activeLang() }, responseType: 'blob' });
export const downloadSupplierInvoiceFiles = (year, month) =>
    api.get('/reports/supplier-invoice-files', { params: { year, month }, responseType: 'blob' });
export const downloadReceiptsZip = (year, month) =>
    api.get('/reports/receipts', { params: { year, month }, responseType: 'blob' });
export const downloadCustomerInvoiceFiles = (year, month) =>
    api.get('/reports/customer-invoice-files', { params: { year, month, lang: _activeLang() }, responseType: 'blob' });
export const downloadCostCentresReport = (year, month) =>
    api.get('/reports/cost-centres', { params: { year, month, lang: _activeLang() }, responseType: 'blob' });

// Pay Slip
export const downloadPayslip = (userId, year, month) =>
    api.get('/reports/payslip', { params: { user_id: userId, year, month, lang: _activeLang() }, responseType: 'blob' });

export const downloadMyPayslip = (year, month) =>
    api.get('/reports/payslip/me', { params: { year, month, lang: _activeLang() }, responseType: 'blob' });

export const getPayslipHistory = () => api.get('/reports/payslip/me/history');

// Email Notification Settings (admin only)
export const getEmailSettings = () => api.get('/admin/email-settings');
export const updateEmailSettings = (data) => api.put('/admin/email-settings', data);

// Time Policy Settings (admin only)
export const getTimeSettings = () => api.get('/admin/time-settings');
export const updateTimeSettings = (data) => api.put('/admin/time-settings', data);

// Flex Elections (per-day banking of overtime as flex)
export const getFlexElections = (month, year) =>
    api.get('/timesheets/flex-elections', { params: { month, year } });
export const createFlexElection = (date) =>
    api.post('/timesheets/flex-election', { date });
export const deleteFlexElection = (date) =>
    api.delete(`/timesheets/flex-election/${date}`);
// Sends one reminder email to the calling admin using the supplied overrides
// merged over the persisted config. Used by the "Send test email" button.
export const sendTestEmail = (overrides) => api.post('/admin/email-settings/send-test', overrides);

// Email Reports (admin only)
export const emailReports = (data) => api.post('/reports/email', data);

// Admin — team monthly submission status
export const getMonthlyStatus = (year, month) =>
    api.get('/admin/timesheets/monthly-status', { params: { year, month } });

// Seed
export const seedData = () => api.post('/seed');

/// FX ///
export const getFxPreview = ({ from, date, amount }) =>
    api.get('/fx/preview', { params: { from, to: 'SEK', date, amount } });

// Fortnox Integration
export const getFortnoxStatus = () => api.get('/fortnox/status');
export const putFortnoxClientConfig = (data) => api.put('/fortnox/client-config', data);
export const getFortnoxAuthStart = () => api.get('/fortnox/auth/start');
export const postFortnoxAuthRevoke = () => api.post('/fortnox/auth/revoke');
export const getFortnoxSettings = () => api.get('/fortnox/settings');
export const putFortnoxSettings = (data) => api.put('/fortnox/settings', data);
export const getFortnoxEmployeeMappings = () => api.get('/fortnox/employee-mappings');
export const putFortnoxEmployeeMappings = (data) => api.put('/fortnox/employee-mappings', data);
export const getFortnoxCustomerMappings = () => api.get('/fortnox/customer-mappings');
export const putFortnoxCustomerMappings = (data) => api.put('/fortnox/customer-mappings', data);
export const getFortnoxSalaryCodes = () => api.get('/fortnox/fortnox-salary-codes');

// Fortnox Sync
export const postFortnoxSyncSalary = (data) => api.post('/fortnox/sync/salary', data);
export const postFortnoxSyncAbsences = (data) => api.post('/fortnox/sync/absences', data);
export const postFortnoxSyncExpenses = (data) => api.post('/fortnox/sync/expenses', data);
export const postFortnoxSyncBilling = (data) => api.post('/fortnox/sync/billing', data);
export const postFortnoxSyncReceipts = (data) => api.post('/fortnox/sync/receipts', data);

// Onboarding Checklist (owner only)
export const getOnboardingStatus = () => api.get('/owner/onboarding/status');
export const postOnboardingDismiss = () => api.post('/owner/onboarding/dismiss');

// Company Settings (owner only)
export const getCompanySettings = () => api.get('/owner/company/settings');
export const updateCompanySettings = (data) => api.put('/owner/company/settings', data);
export const uploadCompanyLogo = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/owner/company/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
export const uploadLoginBackground = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/owner/company/login-background', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

// Company auth settings
export const getCompanyAuthSettings = (companyId = 'hisland') => api.get(`/company/auth-settings?company_id=${companyId}`);
export const updateCompanyAuthSettings = (data) => api.put('/owner/company/auth-settings', data);

// Analytics
export const getAnalyticsOverview = (params) => api.get('/analytics/overview', { params });

// Super Admin
export const listCompanies = () => api.get('/super-admin/companies');
export const listAllUsers = () => api.get('/super-admin/users');
export const createCompany = (data) => api.post('/super-admin/companies', data);
export const toggleCompanyActive = (slug) => api.patch(`/super-admin/companies/${slug}/toggle`);
export const updateCompany = (slug, changes) => api.patch(`/super-admin/companies/${slug}`, changes);
export const deleteCompanyDryRun = (slug) => api.delete(`/super-admin/companies/${slug}?dry_run=true`);
export const deleteCompanyExecute = (slug) => api.delete(`/super-admin/companies/${slug}?dry_run=false`);
export const toggleTestAccountFlag = (userId) => api.put(`/super-admin/users/${userId}/test-flag`);
export const impersonateTestAccount = (userId) => api.post(`/super-admin/impersonate/${userId}`);

// Audit Log (owner — single-tenant, max 200 events)
export const getOwnerAuthEvents = (params = {}) =>
    api.get('/owner/auth-events', { params });

// Audit Log (super-admin — cross-tenant, max 2000 events)
export const getSuperAdminAuthEvents = (params = {}) =>
    api.get('/super-admin/auth-events', { params });

// Named export alongside the default — lets modules that need the raw axios
// instance `import { api }` directly instead of a dynamic-import workaround.
export { api };
export default api;

export const getReceiptFileBlob = (filePath) => api.get(`/expenses/receipt/${filePath}`, { responseType: 'blob' });
